import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useVehicles } from '../../hooks/useVehicles';
import { useCompare } from '../../contexts/CompareContext';
import InventorySEO from './seo';
import VehicleCard from './components/VehicleCard';
import QuickViewModal from './components/QuickViewModal';
import { VehicleCardSkeleton } from '../../components/base/SkeletonCard';
import RequestVehiclePanel from './components/RequestVehiclePanel';
import FilterBar from './components/FilterBar';
import OwnerTeaserStrip from './components/OwnerTeaserStrip';
import RecentlyComparedStrip from './components/RecentlyComparedStrip';
import InventorySearchInput from '../../components/base/InventorySearchInput';
import { useSearchParams, Link } from 'react-router-dom';

type Category = 'all' | 'hot-deals' | 'new-arrivals' | 'suvs' | 'trucks' | 'sedans';
type SortOption = 'price-low' | 'price-high' | 'year-new' | 'year-old' | 'mileage-low';

const DEFAULT_FILTERS = {
  make: [] as string[],
  bodyType: [] as string[],
  minPrice: 0,
  maxPrice: 999999,
  minYear: 1990,
  maxYear: new Date().getFullYear(),
  minMileage: 0,
  maxMileage: 999999,
};

// ── Active Filter Chips (mobile) ──────────────────────────────────────────────
interface ActiveFilterChipsProps {
  filters: typeof DEFAULT_FILTERS;
  onFilterChange: (f: typeof DEFAULT_FILTERS) => void;
}

function ActiveFilterChips({ filters, onFilterChange }: ActiveFilterChipsProps) {
  const chips: { label: string; onRemove: () => void }[] = [];

  filters.make.forEach(m =>
    chips.push({ label: m, onRemove: () => onFilterChange({ ...filters, make: filters.make.filter(x => x !== m) }) })
  );
  filters.bodyType.forEach(bt =>
    chips.push({ label: bt, onRemove: () => onFilterChange({ ...filters, bodyType: filters.bodyType.filter(x => x !== bt) }) })
  );
  if (filters.minPrice > 0 || filters.maxPrice < 999999) {
    const label =
      filters.minPrice > 0 && filters.maxPrice < 999999
        ? `$${(filters.minPrice / 1000).toFixed(0)}k–$${(filters.maxPrice / 1000).toFixed(0)}k`
        : filters.minPrice > 0
        ? `From $${(filters.minPrice / 1000).toFixed(0)}k`
        : `Up to $${(filters.maxPrice / 1000).toFixed(0)}k`;
    chips.push({ label, onRemove: () => onFilterChange({ ...filters, minPrice: 0, maxPrice: 999999 }) });
  }
  if (filters.minYear > 1990 || filters.maxYear < new Date().getFullYear()) {
    chips.push({
      label: `${filters.minYear}–${filters.maxYear}`,
      onRemove: () => onFilterChange({ ...filters, minYear: 1990, maxYear: new Date().getFullYear() }),
    });
  }
  if (filters.minMileage > 0 || filters.maxMileage < 999999) {
    const label =
      filters.maxMileage < 999999
        ? `Under ${(filters.maxMileage / 1000).toFixed(0)}k mi`
        : `Over ${(filters.minMileage / 1000).toFixed(0)}k mi`;
    chips.push({ label, onRemove: () => onFilterChange({ ...filters, minMileage: 0, maxMileage: 999999 }) });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide -mx-1 px-1 sm:hidden">
      {chips.map(chip => (
        <button
          key={chip.label}
          onClick={chip.onRemove}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap flex-shrink-0 cursor-pointer transition-all"
          style={{
            background: '#FEF3C7',
            color: '#92400E',
            border: '1px solid #FCD34D',
          }}
          aria-label={`Remove filter: ${chip.label}`}
        >
          {chip.label}
          <i className="ri-close-line text-[10px]" aria-hidden="true" />
        </button>
      ))}
      {chips.length > 1 && (
        <button
          onClick={() => onFilterChange(DEFAULT_FILTERS)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap flex-shrink-0 cursor-pointer transition-all"
          style={{
            background: '#1A2332',
            color: '#E8B84B',
            border: '1px solid #1A2332',
          }}
        >
          Clear all
        </button>
      )}
    </div>
  );
}

// ── Washington Buyers Info Box ────────────────────────────────────────────────
function WashingtonBuyerInfoBox() {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left cursor-pointer hover:bg-[#DBEAFE]/60 transition-colors focus:outline-none"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center bg-[#1D4ED8] rounded-lg flex-shrink-0">
            <i className="ri-map-pin-2-line text-white text-sm" />
          </div>
          <span className="text-sm font-bold text-[#1E3A5F]">
            Washington Buyer? Here's exactly what to expect — fees in Oregon vs. at your WA DOL
          </span>
        </div>
        <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-[#1D4ED8] text-lg" />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-[#BFDBFE]">
          <p className="text-xs text-[#1E3A5F]/70 mb-4 leading-relaxed">
            Cross-state purchases are one of our specialties. Here's the honest breakdown — no surprises at the DOL.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {/* Oregon column */}
            <div className="bg-white border border-[#BFDBFE] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center bg-[#DCFCE7] rounded-md flex-shrink-0">
                  <i className="ri-store-2-line text-[#16A34A] text-sm" />
                </div>
                <span className="text-xs font-bold text-[#1E3A5F] uppercase tracking-wide">Paid Here in Oregon</span>
              </div>
              <ul className="space-y-1.5 text-xs text-[#374151]">
                <li className="flex justify-between">
                  <span>Vehicle price</span>
                  <span className="font-semibold">As listed</span>
                </li>
                <li className="flex justify-between">
                  <span>Doc fee (OR cap)</span>
                  <span className="font-semibold">$150 max</span>
                </li>
                <li className="flex justify-between">
                  <span>Title fee</span>
                  <span className="font-semibold">~$116</span>
                </li>
                <li className="flex justify-between">
                  <span>OR registration (2-yr)</span>
                  <span className="font-semibold">~$86–$152</span>
                </li>
                <li className="flex justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                  <span className="font-semibold">Oregon sales tax</span>
                  <span className="font-bold text-[#16A34A]">$0</span>
                </li>
              </ul>
              <p className="text-[10px] text-[#6B7280] mt-3 leading-relaxed">
                You drive home on a 30-day Oregon transit permit. No WA plates needed yet.
              </p>
            </div>

            {/* Washington column */}
            <div className="bg-white border border-[#BFDBFE] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center bg-[#FEF3C7] rounded-md flex-shrink-0">
                  <i className="ri-government-line text-[#D97706] text-sm" />
                </div>
                <span className="text-xs font-bold text-[#1E3A5F] uppercase tracking-wide">Paid at Your WA DOL</span>
              </div>
              <ul className="space-y-1.5 text-xs text-[#374151]">
                <li className="flex justify-between">
                  <span>WA sales tax (8.2–10.25%)</span>
                  <span className="font-semibold text-[#D97706]">On purchase price</span>
                </li>
                <li className="flex justify-between">
                  <span>Trade-in deduction</span>
                  <span className="font-semibold text-[#16A34A]">Reduces taxable $</span>
                </li>
                <li className="flex justify-between">
                  <span>WA registration</span>
                  <span className="font-semibold">~$150</span>
                </li>
                <li className="flex justify-between">
                  <span>License plates</span>
                  <span className="font-semibold">~$20</span>
                </li>
              </ul>
              <p className="text-[10px] text-[#6B7280] mt-3 leading-relaxed">
                You have 30 days from purchase to register at your local WA DOL.
              </p>
            </div>
          </div>

          {/* Key callout */}
          <div className="bg-[#FEF9EC] border border-[#FCD34D] rounded-xl p-3.5 mb-3">
            <div className="flex items-start gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-[#D97706] text-base" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#92400E] mb-0.5">You cannot avoid Washington sales tax by buying in Oregon.</p>
                <p className="text-[11px] text-[#78350F] leading-relaxed">
                  Where you <strong>register</strong> is where the taxes apply — no exceptions. Your WA DOL collects it when you plate the vehicle. What you <em>do</em> save: Oregon's doc fee is capped at $150 by law. Many WA dealerships charge $300–$500+. Add our broker pricing (no dealer markup) and that's where the real savings are.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href="/best-awd-cars-oregon-washington#wa-cost-calculator"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1D4ED8] text-white text-xs font-semibold rounded-lg hover:bg-[#1E40AF] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calculator-line text-xs" />
              Full WA Cost Calculator
            </a>
            <a
              href="/contact#washington-buyers"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#BFDBFE] text-[#1D4ED8] text-xs font-semibold rounded-lg hover:bg-[#EFF6FF] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-answer-line text-xs" />
              WA Buyer FAQ
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function InventoryPage() {
  const { vehicles, loading } = useVehicles();
  const { compareIds } = useCompare();
  const compareActive = compareIds.length > 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const [sortBy, setSortBy] = useState<SortOption>('price-low');
  const [requestPanelOpen, setRequestPanelOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [quickViewVehicle, setQuickViewVehicle] = useState<any>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const scrollToResults = useCallback(() => {
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, []);

  // ── Read URL params on mount and apply as initial filters ─────────────────
  useEffect(() => {
    const makeParam = searchParams.get('make');
    const bodyTypeParam = searchParams.get('bodyType');
    const minPriceParam = searchParams.get('minPrice');
    const maxPriceParam = searchParams.get('maxPrice');
    const minYearParam = searchParams.get('minYear');
    const maxYearParam = searchParams.get('maxYear');
    const minMileageParam = searchParams.get('minMileage');
    const maxMileageParam = searchParams.get('maxMileage');
    const searchParam = searchParams.get('search');
    const categoryParam = searchParams.get('category');

    const newFilters = { ...DEFAULT_FILTERS };
    let hasParams = false;

    if (makeParam) {
      newFilters.make = makeParam.split(',').map(m => m.trim()).filter(Boolean);
      hasParams = true;
    }
    if (bodyTypeParam) {
      newFilters.bodyType = bodyTypeParam.split(',').map(b => b.trim()).filter(Boolean);
      hasParams = true;
    }
    if (minPriceParam) { newFilters.minPrice = Number(minPriceParam); hasParams = true; }
    if (maxPriceParam) { newFilters.maxPrice = Number(maxPriceParam); hasParams = true; }
    if (minYearParam)  { newFilters.minYear  = Number(minYearParam);  hasParams = true; }
    if (maxYearParam)  { newFilters.maxYear  = Number(maxYearParam);  hasParams = true; }
    if (minMileageParam) { newFilters.minMileage = Number(minMileageParam); hasParams = true; }
    if (maxMileageParam) { newFilters.maxMileage = Number(maxMileageParam); hasParams = true; }

    if (hasParams) setFilters(newFilters);
    if (searchParam) setSearchQuery(searchParam);
    if (categoryParam) setSelectedCategory(categoryParam as Category);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  // ── Sync filters back to URL so links stay shareable ──────────────────────
  useEffect(() => {
    const params: Record<string, string> = {};

    if (filters.make.length > 0)     params.make     = filters.make.join(',');
    if (filters.bodyType.length > 0) params.bodyType = filters.bodyType.join(',');
    if (filters.minPrice > 0)        params.minPrice = String(filters.minPrice);
    if (filters.maxPrice < 999999)   params.maxPrice = String(filters.maxPrice);
    if (filters.minYear > 1990)      params.minYear  = String(filters.minYear);
    if (filters.maxYear < new Date().getFullYear()) params.maxYear = String(filters.maxYear);
    if (filters.minMileage > 0)      params.minMileage = String(filters.minMileage);
    if (filters.maxMileage < 999999) params.maxMileage = String(filters.maxMileage);
    if (searchQuery.trim())          params.search   = searchQuery.trim();
    if (selectedCategory !== 'all')  params.category = selectedCategory;

    setSearchParams(params, { replace: true });
  }, [filters, searchQuery, selectedCategory, setSearchParams]);

  const categorized = useMemo(() => {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return {
      hotDeals:    vehicles.filter(v => v.hot_deal === true),
      newArrivals: vehicles.filter(v => v.created_at && new Date(v.created_at).getTime() > thirtyDaysAgo),
      suvs:        vehicles.filter(v => v.bodyType?.toLowerCase().includes('suv')),
      trucks:      vehicles.filter(v => ['truck','pickup'].some(t => v.bodyType?.toLowerCase().includes(t))),
      sedans:      vehicles.filter(v => v.bodyType?.toLowerCase().includes('sedan')),
    };
  }, [vehicles]);

  const filteredVehicles = useMemo(() => {
    let result = [...vehicles];

    switch (selectedCategory) {
      case 'hot-deals':    result = categorized.hotDeals;    break;
      case 'new-arrivals': result = categorized.newArrivals; break;
      case 'suvs':         result = categorized.suvs;        break;
      case 'trucks':       result = categorized.trucks;      break;
      case 'sedans':       result = categorized.sedans;      break;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(v =>
        `${v.year} ${v.make} ${v.model} ${v.trim || ''}`.toLowerCase().includes(q)
      );
    }

    if (filters.make.length > 0) {
      result = result.filter(v => filters.make.includes(v.make));
    }
    if (filters.bodyType.length > 0) {
      result = result.filter(v => filters.bodyType.some(bt => v.bodyType?.toLowerCase().includes(bt.toLowerCase())));
    }
    result = result.filter(v => v.price >= filters.minPrice && v.price <= filters.maxPrice);
    result = result.filter(v => v.year >= filters.minYear && v.year <= filters.maxYear);
    result = result.filter(v => v.mileage >= filters.minMileage && v.mileage <= filters.maxMileage);

    switch (sortBy) {
      case 'price-low':   result.sort((a, b) => a.price - b.price);     break;
      case 'price-high':  result.sort((a, b) => b.price - a.price);     break;
      case 'year-new':    result.sort((a, b) => b.year - a.year);       break;
      case 'year-old':    result.sort((a, b) => a.year - b.year);       break;
      case 'mileage-low': result.sort((a, b) => a.mileage - b.mileage); break;
    }
    return result;
  }, [vehicles, selectedCategory, categorized, sortBy, filters, searchQuery]);

  const activeFilterCount =
    filters.make.length +
    filters.bodyType.length +
    (filters.minPrice > 0 || filters.maxPrice < 999999 ? 1 : 0) +
    (filters.minYear > 1990 || filters.maxYear < new Date().getFullYear() ? 1 : 0) +
    (filters.minMileage > 0 || filters.maxMileage < 999999 ? 1 : 0);

  const seo = <InventorySEO />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        {seo}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10">
          <div className="h-8 w-48 bg-slate-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-72 bg-slate-100 rounded animate-pulse mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => <VehicleCardSkeleton key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        {seo}
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
            <i className="ri-car-line text-5xl text-slate-300" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A2332] mb-4">No Vehicles Available</h1>
          <p className="text-slate-500 mb-8">We&apos;re updating our inventory. Check back soon or request a specific vehicle.</p>
          <button
            onClick={() => setRequestPanelOpen(true)}
            className="px-8 py-4 bg-[#E8B84B] text-[#1A2332] font-bold rounded-lg hover:bg-[#d4a43a] transition-colors cursor-pointer whitespace-nowrap"
          >
            Find My Car
          </button>
        </div>
        <RequestVehiclePanel isOpen={requestPanelOpen} onClose={() => setRequestPanelOpen(false)} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F7F5F0]"
      style={compareActive ? { paddingBottom: '178px' } : undefined}
    >
      {seo}

      {/* ── Hero Section ──────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1a0e48de72e97b22ef57725106789636.jpg"
            alt=""
            className="w-full h-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1520]/80 via-[#0d1520]/70 to-[#0d1520]/95" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8B84B] to-transparent opacity-60" />
        </div>

        {/* Desktop Hero */}
        <div className="relative hidden sm:block max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1 h-3 bg-[#E8B84B] rounded-full" />
                <p className="text-[#E8B84B] text-[10px] font-bold uppercase tracking-widest">
                  Rockhound Auto · Warrenton, OR
                </p>
              </div>
              <h1 className="text-2xl lg:text-3xl font-black text-white leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Browse Our Inventory
              </h1>
              <p className="text-slate-500 text-xs mt-0.5">
                {vehicles.length} vehicles available — Pacific Northwest sourced, broker-priced
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              {[
                { icon: 'ri-shield-check-line', text: '100% Inspected' },
                { icon: 'ri-price-tag-3-line', text: 'Broker Priced' },
                { icon: 'ri-truck-line', text: 'Free Delivery' },
              ].map(b => (
                <div key={b.text} className="flex items-center gap-1.5 bg-white/10 border border-white/15 rounded-full px-3 py-1">
                  <i className={`${b.icon} text-[#E8B84B] text-xs`} />
                  <span className="text-white/75 text-xs font-medium whitespace-nowrap">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Hero */}
        <div className="relative sm:hidden px-4 pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className="w-0.5 h-3 bg-[#E8B84B] rounded-full" />
              <div>
                <p className="text-[#E8B84B] text-[9px] font-bold uppercase tracking-widest leading-none">
                  Rockhound Auto · Warrenton, OR
                </p>
                <h1 className="text-xl font-black text-white leading-tight mt-0.5" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Browse <span className="text-[#E8B84B]">Inventory</span>
                </h1>
              </div>
            </div>
            <div className="bg-[#E8B84B] rounded-lg px-2.5 py-1.5 text-center shadow-lg shadow-[#E8B84B]/40 flex-shrink-0">
              <span className="text-[#1A2332] text-lg font-black leading-none block" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {vehicles.length}
              </span>
              <span className="text-[#1A2332]/70 text-[8px] font-bold uppercase tracking-wide">vehicles</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {[
              { icon: 'ri-shield-check-line', text: '100% Inspected' },
              { icon: 'ri-price-tag-3-line', text: 'Broker Priced' },
              { icon: 'ri-truck-line', text: 'Free Delivery' },
            ].map(b => (
              <div key={b.text} className="flex items-center gap-1 bg-white/10 border border-white/15 rounded-full px-2.5 py-1">
                <i className={`${b.icon} text-[#E8B84B] text-[10px]`} />
                <span className="text-white/75 text-[10px] font-medium whitespace-nowrap">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Search Bar ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-2">

          {/* Mobile */}
          <div className="flex flex-col gap-2 sm:hidden py-2">

            {/* Row 1: Search — full width */}
            <InventorySearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={scrollToResults}
              vehicles={vehicles}
              placeholder="Search make, model, year…"
            />

            {/* Row 2: Sort + Filter + count */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortOption)}
                className="flex-1 px-3 py-2.5 bg-[#F7F5F0] text-[#1A2332] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8B84B] text-sm cursor-pointer"
              >
                <option value="price-low">Price ↑</option>
                <option value="price-high">Price ↓</option>
                <option value="year-new">Newest</option>
                <option value="year-old">Oldest</option>
                <option value="mileage-low">Miles ↑</option>
              </select>
              <FilterBar
                filters={filters}
                onFilterChange={setFilters}
                vehicleCount={filteredVehicles.length}
                isMobile
              />
              <span className="text-sm text-slate-500 whitespace-nowrap flex-shrink-0">
                <span className="font-semibold text-[#1A2332]">{filteredVehicles.length}</span> cars
              </span>
            </div>

            {/* Active filter chips */}
            <ActiveFilterChips filters={filters} onFilterChange={setFilters} />
          </div>

          {/* Desktop — single compact row */}
          <div className="hidden sm:flex items-center gap-2">
            <InventorySearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={scrollToResults}
              vehicles={vehicles}
              placeholder="Search make, model…"
              className="w-64"
              size="sm"
            />
            <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
            <div className="flex items-center gap-1.5">
              <Link
                to="/best-awd-cars-oregon-washington#wa-cost-calculator"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1D4ED8] text-white text-xs font-semibold rounded-lg hover:bg-[#1E40AF] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calculator-line" />
                Full WA Cost Calculator
              </Link>
              <Link
                to="/contact#washington-buyers"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#BFDBFE] text-[#1D4ED8] text-xs font-semibold rounded-lg hover:bg-[#EFF6FF] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-question-answer-line" />
                WA Buyer FAQ
              </Link>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-slate-400 text-xs">
                <span className="text-[#1A2332] font-semibold">{filteredVehicles.length}</span> vehicles
              </span>
              {/* Sort only shown on sm–lg; lg+ has its own sort in the results toolbar */}
              <div className="lg:hidden">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortOption)}
                  className="px-2.5 py-1.5 bg-[#F7F5F0] text-[#1A2332] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8B84B] text-xs cursor-pointer"
                >
                  <option value="price-low">Price: Low → High</option>
                  <option value="price-high">Price: High → Low</option>
                  <option value="year-new">Newest First</option>
                  <option value="year-old">Oldest First</option>
                  <option value="mileage-low">Lowest Mileage</option>
                </select>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Body: Sidebar + Grid ─────────────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">

        <div className="flex gap-7 items-start">

          {/* Sidebar — desktop only */}
          <aside className="hidden lg:block flex-shrink-0 w-72">
            <FilterBar
              filters={filters}
              onFilterChange={setFilters}
              vehicleCount={filteredVehicles.length}
            />
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div ref={resultsRef} className="mb-6">
              <div className="hidden lg:flex items-center justify-between gap-3">
                <p className="text-slate-500 text-sm">
                  <span className="text-[#1A2332] font-semibold">{filteredVehicles.length}</span>
                  {' '}{filteredVehicles.length === 1 ? 'vehicle' : 'vehicles'}
                  {activeFilterCount > 0 && (
                    <button onClick={() => setFilters(DEFAULT_FILTERS)} className="ml-2 text-xs text-[#E8B84B] hover:underline cursor-pointer">
                      Clear filters
                    </button>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as SortOption)}
                    className="px-2.5 py-1.5 bg-[#F7F5F0] text-[#1A2332] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8B84B] text-xs cursor-pointer"
                  >
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="year-new">Newest First</option>
                    <option value="year-old">Oldest First</option>
                    <option value="mileage-low">Lowest Mileage</option>
                  </select>
                  <button
                    onClick={() => setRequestPanelOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#E8B84B] text-[#1A2332] font-bold rounded-lg hover:bg-[#d4a43a] transition-colors whitespace-nowrap text-sm cursor-pointer"
                  >
                    <i className="ri-add-line" />
                    Find My Car
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 lg:hidden">
                <button
                  onClick={() => { setSelectedCategory('all'); setFilters(DEFAULT_FILTERS); setSearchQuery(''); }}
                  className="px-3 py-1.5 bg-[#1A2332] text-white font-semibold rounded-lg hover:bg-[#2d3f55] transition-colors text-xs whitespace-nowrap cursor-pointer"
                >
                  See Everything
                </button>
                <button
                  onClick={() => setRequestPanelOpen(true)}
                  className="px-3 py-1.5 bg-[#E8B84B] text-[#1A2332] font-bold rounded-lg hover:bg-[#d4a43a] transition-colors text-xs whitespace-nowrap cursor-pointer"
                >
                  {filters.maxPrice < 999999
                    ? `Under $${(filters.maxPrice / 1000).toFixed(0)}k`
                    : 'Find My Car'}
                </button>
                <Link
                  to="/deal-radar"
                  className="px-3 py-1.5 border border-slate-300 text-[#1A2332] font-semibold rounded-lg hover:border-[#E8B84B] hover:bg-[#FFFBF0] transition-colors text-xs whitespace-nowrap cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-radar-line text-xs" />
                  Deal Alerts
                </Link>
              </div>
            </div>

            {/* Grid or Empty State */}
            {filteredVehicles.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-slate-100 flex items-center justify-center">
                  <i className="ri-search-line text-3xl text-slate-400" />
                </div>

                {filters.maxPrice < 999999 ? (
                  <>
                    <h3 className="text-xl font-bold text-[#1A2332] mb-2">
                      No vehicles under ${filters.maxPrice.toLocaleString()} right now
                    </h3>
                    <p className="text-slate-500 mb-3 text-sm max-w-md mx-auto">
                      Our under-${filters.maxPrice.toLocaleString()} inventory moves fast — we source new vehicles every week across the Pacific Northwest. Let us notify you the moment one lands, or tell us exactly what you need and we'll find it.
                    </p>
                    <div className="inline-flex items-center gap-2 bg-[#FEF9EC] border border-[#FCD34D] rounded-lg px-4 py-2.5 mb-8 text-sm text-[#92400E]">
                      <i className="ri-information-line text-[#D97706]" />
                      <span>Our cheapest current vehicle is <strong>${Math.min(...vehicles.map(v => v.price)).toLocaleString()}</strong> — try raising your budget or request a specific vehicle.</span>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-[#1A2332] mb-2">No vehicles match your filters</h3>
                    <p className="text-slate-500 mb-8 text-sm max-w-md mx-auto">
                      We source vehicles across the Pacific Northwest every week — let us find exactly what you need.
                    </p>
                  </>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => { setSelectedCategory('all'); setFilters(DEFAULT_FILTERS); setSearchQuery(''); }}
                    className="px-6 py-3 bg-[#1A2332] text-white font-semibold rounded-lg hover:bg-[#2d3f55] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    See Everything We Have
                  </button>
                  <button
                    onClick={() => setRequestPanelOpen(true)}
                    className="px-6 py-3 bg-[#E8B84B] text-[#1A2332] font-bold rounded-lg hover:bg-[#d4a43a] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    {filters.maxPrice < 999999
                      ? `Find Me a Car Under $${filters.maxPrice.toLocaleString()}`
                      : 'Find My Car'}
                  </button>
                  <Link
                    to="/deal-radar"
                    className="px-6 py-3 border border-slate-300 text-[#1A2332] font-semibold rounded-lg hover:border-[#E8B84B] hover:bg-[#FFFBF0] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-radar-line mr-1.5" />
                    Get Deal Alerts
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredVehicles.map((vehicle, i) => (
                    <VehicleCard
                      key={vehicle.uuid}
                      vehicle={vehicle}
                      priority={i === 0}
                      onQuickView={setQuickViewVehicle}
                    />
                  ))}
                </div>

                {/* ── Grid Footer: Back to Top (mobile) ── */}
                <div className="mt-8 flex items-center justify-center sm:hidden">
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer transition-all active:scale-95"
                    style={{
                      background: '#1A2332',
                      color: '#E8B84B',
                      border: '1px solid rgba(232,184,75,0.25)',
                      boxShadow: '0 2px 10px rgba(26,37,64,0.18)',
                    }}
                    aria-label="Back to top of inventory"
                  >
                    <i className="ri-arrow-up-line text-base" aria-hidden="true" />
                    Back to top
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Post-grid contextual sections ───────────────────────────────── */}
        <div className="mt-10 space-y-4">
          <RecentlyComparedStrip />
          <OwnerTeaserStrip />
          <WashingtonBuyerInfoBox />
        </div>

      </div>

      {/* ── Bottom CTA ───────────────────────────────────────────────────── */}
      <div className="bg-[#1A2332] py-14 mt-4">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-[#E8B84B]/20">
            <i className="ri-customer-service-2-line text-2xl text-[#E8B84B]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Can&apos;t Find What You&apos;re Looking For?
          </h2>
          <p className="text-white/60 mb-6 text-sm sm:text-base max-w-xl mx-auto">
            Tell us exactly what you want. Our broker team sources vehicles across the Pacific Northwest — often below market price.
          </p>
          <button
            onClick={() => setRequestPanelOpen(true)}
            className="px-8 py-3.5 bg-[#E8B84B] text-[#1A2332] font-bold rounded-lg hover:bg-[#d4a43a] transition-colors cursor-pointer whitespace-nowrap"
          >
            Find My Car — We'll Source It
          </button>
        </div>
      </div>

      <RequestVehiclePanel isOpen={requestPanelOpen} onClose={() => setRequestPanelOpen(false)} />

      <QuickViewModal
        vehicle={quickViewVehicle}
        onClose={() => setQuickViewVehicle(null)}
      />
    </div>
  );
}
